import React, { useEffect, useRef } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import './GEM.css';

const GEM = () => {
  const { t } = useTranslation();
  const refs = useRef([]);

  const gemCategories = [
    {
      icon: '📷',
      title: t('gem.categories.cctv.title'),
      items: t('gem.categories.cctv.items'),
      tag: t('gem.categories.cctv.tag'),
    },
    {
      icon: '📎',
      title: t('gem.categories.stationery.title'),
      items: t('gem.categories.stationery.items'),
      tag: t('gem.categories.stationery.tag'),
    },
    {
      icon: '💡',
      title: t('gem.categories.electrical.title'),
      items: t('gem.categories.electrical.items'),
      tag: t('gem.categories.electrical.tag'),
    },
    {
      icon: '🖥️',
      title: t('gem.categories.computers.title'),
      items: t('gem.categories.computers.items'),
      tag: t('gem.categories.computers.tag'),
    },
    {
      icon: '🧹',
      title: t('gem.categories.cleaning.title'),
      items: t('gem.categories.cleaning.items'),
      tag: t('gem.categories.cleaning.tag'),
    },
    {
      icon: '🪑',
      title: t('gem.categories.furniture.title'),
      items: t('gem.categories.furniture.items'),
      tag: t('gem.categories.furniture.tag'),
    },
  ];

  const govtClients = t('gem.clients.list');

  const advantages = [
    { icon: '✅', title: t('gem.advantages.adv1.title'), desc: t('gem.advantages.adv1.desc') },
    { icon: '🚚', title: t('gem.advantages.adv2.title'), desc: t('gem.advantages.adv2.desc') },
    { icon: '📋', title: t('gem.advantages.adv3.title'), desc: t('gem.advantages.adv3.desc') },
    { icon: '💯', title: t('gem.advantages.adv4.title'), desc: t('gem.advantages.adv4.desc') },
    { icon: '🔄', title: t('gem.advantages.adv5.title'), desc: t('gem.advantages.adv5.desc') },
    { icon: '🤝', title: t('gem.advantages.adv6.title'), desc: t('gem.advantages.adv6.desc') },
  ];
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => entries.forEach(e => e.isIntersecting && e.target.classList.add('visible')),
      { threshold: 0.07 }
    );
    refs.current.forEach(el => el && observer.observe(el));
    return () => observer.disconnect();
  }, []);
  const add = el => { if (el && !refs.current.includes(el)) refs.current.push(el); };

  return (
    <div className="gem-page">
      {/* GEM HERO */}
      <section className="gem-hero">
        <div className="gem-hero__bg" />
        <div className="container gem-hero__inner">
          <div className="gem-hero__content reveal" ref={add}>
            <div className="gem-hero__badge">
              <span>🏛️</span>
              {t('gem.hero.badge')}
            </div>
            <div className="vocal-local-badge vocal-local-badge--light">
              <span>🇮🇳</span>
              <span className="vocal-text">{t('home.hero.vocalForLocal') || 'Vocal for Local'}</span>
              <span className="vocal-divider">|</span>
              <span className="vocal-sub">{t('home.hero.kolhapursOwn') || "Kolhapur's Own"}</span>
            </div>
            <h1 className="gem-hero__title">
              {t('gem.hero.title1')}<br />
              <em>{t('gem.hero.title2')}</em> {t('gem.hero.title3')}
            </h1>
            <p className="gem-hero__sub">
              {t('gem.hero.subtitle')}
            </p>
            <div className="gem-hero__actions">
              <a href="tel:+919221722575" className="btn btn-saffron">
                {t('gem.hero.callForEnquiry')}
              </a>
              <a href="mailto:gem@shivshrienterprises.in" className="btn btn-outline-white">
                {t('gem.hero.emailGem')}
              </a>
            </div>
            <div className="gem-hero__trust">
              {[t('gem.hero.activeReg'), t('gem.hero.gstReg'), t('gem.hero.msmeReg'), t('gem.hero.yearsExp')].map(trust => (
                <div key={trust} className="gem-trust-pill">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>
                  {trust}
                </div>
              ))}
            </div>
          </div>
          <div className="gem-hero__card reveal-right" ref={add}>
            <div className="gem-info-card">
              <div className="gem-info-header">
                <div className="gem-info-logo">🏛️</div>
                <div>
                  <strong>{t('gem.hero.infoCard.header')}</strong>
                  <span>{t('gem.hero.infoCard.subheader')}</span>
                </div>
              </div>
              <div className="gem-info-rows">
                <div className="gem-info-row">
                  <span className="gem-info-label">{t('gem.hero.infoCard.supplierName')}</span>
                  <span className="gem-info-value">{t('common.companyNameFull')}</span>
                </div>
                <div className="gem-info-row">
                  <span className="gem-info-label">{t('gem.hero.infoCard.sellerType')}</span>
                  <span className="gem-info-value">{t('gem.hero.infoCard.sellerValue')}</span>
                </div>
                <div className="gem-info-row">
                  <span className="gem-info-label">{t('gem.hero.infoCard.state')}</span>
                  <span className="gem-info-value">{t('gem.hero.infoCard.stateValue')}</span>
                </div>
                <div className="gem-info-row">
                  <span className="gem-info-label">{t('gem.hero.infoCard.district')}</span>
                  <span className="gem-info-value">{t('gem.hero.infoCard.districtValue')}</span>
                </div>
                <div className="gem-info-row">
                  <span className="gem-info-label">{t('gem.hero.infoCard.headOffice')}</span>
                  <span className="gem-info-value">{t('gem.hero.infoCard.headOfficeValue')}</span>
                </div>
                <div className="gem-info-row">
                  <span className="gem-info-label">{t('gem.hero.infoCard.gemStatus')}</span>
                  <span className="gem-info-value gem-status-active">{t('gem.hero.infoCard.gemStatusValue')}</span>
                </div>
                <div className="gem-info-row">
                  <span className="gem-info-label">{t('gem.hero.infoCard.gstRegistered')}</span>
                  <span className="gem-info-value">{t('gem.hero.infoCard.gstValue')}</span>
                </div>
                <div className="gem-info-row">
                  <span className="gem-info-label">{t('gem.hero.infoCard.serviceArea')}</span>
                  <span className="gem-info-value">{t('gem.hero.infoCard.serviceAreaValue')}</span>
                </div>
              </div>
              <a href="mailto:gem@shivshrienterprises.in" className="btn btn-saffron gem-info-cta">{t('gem.hero.infoCard.ctaButton')}</a>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT WE SUPPLY */}
      <section className="section-padding" style={{ background: 'white' }}>
        <div className="container">
          <div className="text-center reveal" ref={add} style={{ marginBottom: '3rem' }}>
            <div className="section-badge saffron">{t('gem.categories.badge')}</div>
            <h2>{t('gem.categories.title')}</h2>
            <p style={{ color: 'var(--text-mid)', marginTop: '0.5rem', maxWidth: '580px', margin: '0.5rem auto 0' }}>
              {t('gem.hero.subtitle')}
            </p>
          </div>
          <div className="gem-categories">
            {gemCategories.map((cat, i) => (
              <div key={cat.title} className="gem-category-card reveal" ref={add} style={{ transitionDelay: `${i * 0.08}s` }}>
                <div className="gem-cat-header">
                  <div className="gem-cat-icon">{cat.icon}</div>
                  <div>
                    <div className="gem-cat-tag">{cat.tag}</div>
                    <h3 className="gem-cat-title">{cat.title}</h3>
                  </div>
                </div>
                <ul className="gem-cat-items">
                  {cat.items.map(item => (
                    <li key={item}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--saffron)" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GOVT CLIENTS */}
      <section className="section-padding" style={{ background: 'var(--saffron-light)', borderTop: '1px solid rgba(249,115,22,0.15)', borderBottom: '1px solid rgba(249,115,22,0.15)' }}>
        <div className="container">
          <div className="text-center reveal" ref={add} style={{ marginBottom: '3rem' }}>
            <div className="section-badge saffron">{t('gem.clients.badge')}</div>
            <h2>{t('gem.clients.title')}</h2>
          </div>
          <div className="govt-clients-grid reveal" ref={add}>
            {govtClients.map(c => (
              <div key={c} className="govt-client-pill">
                <span>🏛️</span> {c}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ADVANTAGES */}
      <section className="section-padding" style={{ background: 'white' }}>
        <div className="container">
          <div className="text-center reveal" ref={add} style={{ marginBottom: '3rem' }}>
            <div className="section-badge">{t('gem.advantages.badge')}</div>
            <h2>{t('gem.advantages.title')}</h2>
          </div>
          <div className="gem-advantages-grid">
            {advantages.map((a, i) => (
              <div key={a.title} className="gem-advantage-card reveal" ref={add} style={{ transitionDelay: `${i * 0.1}s` }}>
                <div className="gem-advantage-icon">{a.icon}</div>
                <h4 className="gem-advantage-title">{a.title}</h4>
                <p className="gem-advantage-desc">{a.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW TO ORDER */}
      <section className="section-padding" style={{ background: 'var(--navy)' }}>
        <div className="container">
          <div className="text-center reveal" ref={add} style={{ marginBottom: '3rem' }}>
            <div className="section-badge" style={{ background: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.7)', borderColor: 'rgba(255,255,255,0.15)' }}>{t('gem.procurement.badge')}</div>
            <h2 style={{ color: 'white' }}>{t('gem.procurement.title')}</h2>
          </div>
          <div className="gem-order-steps">
            {t('gem.procurement.steps').map((s, i) => (
              <div key={s.step} className="gem-order-step reveal" ref={add} style={{ transitionDelay: `${i * 0.1}s` }}>
                <div className="gem-order-num">{s.step}</div>
                <h4 className="gem-order-title">{s.title}</h4>
                <p className="gem-order-desc">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT FOR GEM */}
      <section className="section-padding" style={{ background: 'var(--off-white)', borderTop: '1px solid var(--border)' }}>
        <div className="container">
          <div className="gem-contact reveal" ref={add}>
            <div>
              <div className="section-badge saffron">{t('gem.enquiries.badge')}</div>
              <h2>{t('gem.enquiries.title')}</h2>
              <p style={{ color: 'var(--text-mid)', marginTop: '0.8rem', marginBottom: '1.5rem', fontSize: '0.95rem', lineHeight: '1.75' }}>
                {t('gem.enquiries.subtitle')}
              </p>
              <div className="gem-contact-items">
                <a href="tel:+919221722575" className="gem-contact-item">
                  <div className="gem-contact-icon">📞</div>
                  <div>
                    <strong>{t('gem.enquiries.phone.label')}</strong>
                    <span>{t('gem.enquiries.phone.value')}</span>
                  </div>
                </a>
                <a href="mailto:shivshrienterprises2628@gmail.com" className="gem-contact-item">
                  <div className="gem-contact-icon">✉️</div>
                  <div>
                    <strong>{t('gem.enquiries.email.label')}</strong>
                    <span>{t('gem.enquiries.email.value')}</span>
                  </div>
                </a>
                <div className="gem-contact-item">
                  <div className="gem-contact-icon">📍</div>
                  <div>
                    <strong>{t('gem.enquiries.office.label')}</strong>
                    <span>{t('gem.enquiries.office.value')}</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="gem-contact-form-wrap">
              <div className="gem-contact-form">
                <h3 style={{ fontFamily: 'Fraunces', color: 'var(--navy)', marginBottom: '0.3rem' }}>{t('gem.enquiries.form.title')}</h3>
                <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginBottom: '1.5rem' }}>{t('gem.enquiries.form.subtitle')}</p>
                <div className="gem-form-group">
                  <label>{t('gem.enquiries.form.department')}</label>
                  <input type="text" placeholder={t('gem.enquiries.form.departmentPlaceholder')} />
                </div>
                <div className="gem-form-group">
                  <label>{t('gem.enquiries.form.contactPerson')}</label>
                  <input type="text" placeholder={t('gem.enquiries.form.contactPersonPlaceholder')} />
                </div>
                <div className="gem-form-group">
                  <label>{t('gem.enquiries.form.phone')}</label>
                  <input type="tel" placeholder={t('gem.enquiries.form.phonePlaceholder')} />
                </div>
                <div className="gem-form-group">
                  <label>{t('gem.enquiries.form.products')}</label>
                  <textarea rows="4" placeholder={t('gem.enquiries.form.productsPlaceholder')}></textarea>
                </div>
                <button className="btn btn-saffron" style={{ width: '100%', justifyContent: 'center' }} onClick={e => { e.target.textContent = t('gem.enquiries.form.buttonSuccess'); setTimeout(() => { e.target.textContent = t('gem.enquiries.form.button'); }, 3000); }}>
                  {t('gem.enquiries.form.button')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default GEM;
